/**
 * The two pieces a sign-in page needs that no other public page does (2026-10-05, `login`).
 *
 * `Stage` is the page: a SHOW half (a muted, looping video under a wash, and the brand's welcome
 * over it) beside the half that does the work. The show half is decoration and steps aside below
 * 1024px, where a phone needs the buttons on its first screen; under "reduce motion" the video
 * pauses on its current frame rather than vanishing, so the composition is the same, just still.
 * No `video` means no footage at all: an app still deciding which clip to play passes none, and the
 * half shows only the wash until it has an answer. A clip fades in once its first frame is decoded.
 *
 * `StandIn` is a third-party control drawn as one of ours: the FACE is an anubis control, and the
 * real control (Google's sign-in iframe) lies over it at zero opacity and takes the click. A
 * programmatic `.click()` on a hidden widget does not work across browsers, and Google's own button
 * would be the one control on the page in somebody else's chrome.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { cx } from './cx';
import { Skeleton } from './skeleton';

/** A video the page may play: the brand's (https) or the app's own file (a path). */
function playable(src: string | null | undefined): string {
  const value = (src ?? '').trim();
  return value.startsWith('https://') || value.startsWith('/') ? value : '';
}

/** The connection the browser reports — Chromium's Network Information API; absent elsewhere. */
export interface Connection {
  saveData?: boolean | undefined;
  effectiveType?: string | undefined;
}

/** Below this width the show half is hidden (anubis.css), so its footage would be bytes nobody sees. */
export const STAGE_WIDE = '(min-width: 1024px)';
const SLOW = new Set(['slow-2g', '2g', '3g']);

/**
 * Whether the footage is worth its megabytes: the show half is on screen, the visitor has not asked
 * to save data, and the connection is not a slow one. An unknown connection (Safari, Firefox) plays.
 */
export function playsFootage(wide: boolean, connection: Connection | undefined): boolean {
  if (!wide || connection?.saveData) return false;
  return !SLOW.has(connection?.effectiveType ?? '');
}

/** `playsFootage` for this browser, re-asked when the window crosses the breakpoint. `false` until
 * mounted, so the server markup and the first paint never start a download. */
function useFootage(): boolean {
  const [plays, setPlays] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(STAGE_WIDE);
    const connection = (navigator as Navigator & { connection?: Connection }).connection;
    const apply = () => setPlays(playsFootage(query.matches, connection));
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  return plays;
}

export function Stage({
  video,
  poster,
  show,
  children,
}: {
  /** The footage behind the welcome — the brand's, or the app's fallback file. */
  video?: string | null | undefined;
  /** Its first frame, so the half never flashes another picture while the file arrives. */
  poster?: string | null | undefined;
  /** What sits over the footage: the welcome, the line, the domain. */
  show?: ReactNode | undefined;
  /** The half that does the work. */
  children?: ReactNode | undefined;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  // A phone (no show half), data-saver or a slow link gets the wash over the brand's ground instead.
  const src = useFootage() ? playable(video) : '';
  const still = playable(poster);
  // The footage stays invisible until its first frame is decoded, so the half shows the plain wash
  // while the file arrives — never a black box, and never a frame of something else (2026-10-10).
  // A poster is the clip's own first frame, so a video that has one may show from the start.
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(false), [src]);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      const element = ref.current;
      if (!element) return;
      if (query.matches) element.pause();
      else void element.play().catch(() => undefined);
    };
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [src]);
  return (
    <div className="a-stage">
      <section className="a-stage__show">
        {src ? (
          <video
            key={src}
            ref={ref}
            className="a-stage__video"
            src={src}
            {...(still ? { poster: still } : {})}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            data-ready={ready || Boolean(still) ? 'true' : 'false'}
            onLoadedData={() => setReady(true)}
          />
        ) : null}
        <div className="a-stage__wash" aria-hidden="true" />
        <div className="a-stage__art">{show}</div>
      </section>
      <main className="a-stage__main">{children}</main>
    </div>
  );
}

/**
 * A third-party control worn as one of ours — see the module docstring.
 *
 * `wait` (2026-10-20, owner: *"don't make the buttons available until the user can click them; show
 * skeletons instead"*): until a frame inside the control has LOADED, the face is a button skeleton
 * and the control is mounted but invisible and inert, so a press can never land on a face with
 * nothing under it. Google's sign-in frame arrives a moment after the page; pressed before it, the
 * face did nothing — the "Google sign-in is not working" report.
 *
 * `children` may be a function of the stand-in's width in px: Google draws its button at a FIXED
 * width (200–400) inside its frame, so the control is asked for exactly the face's width — otherwise
 * the part of the face past Google's button is a dead strip (a phone's face was 358px, Google's 300).
 */
/** How long the box must hold one width before a control is drawn at it. */
const STAND_IN_SETTLE_MS = 150;
/** A smaller change than this is layout jitter, not a new width. */
const STAND_IN_WIDTH_STEP = 8;

export function StandIn({
  face,
  wait,
  children,
}: {
  face: ReactNode;
  wait?: boolean | undefined;
  children?: ReactNode | ((width: number) => ReactNode);
}) {
  const box = useRef<HTMLDivElement>(null);
  const control = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(!wait);

  // The width the control is DRAWN at: handed over only once the box has stopped resizing, and
  // only for a real change. A control that sizes itself from it (Google's sign-in button) replaces
  // its whole frame on every new width, and the box measures twice while the card lays out (378px,
  // then 286px on 2026-10-31) — each redraw was a dead window in which a press landed on nothing.
  useEffect(() => {
    const element = box.current;
    if (!element) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = (next: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setWidth((was) => (Math.abs(was - next) >= STAND_IN_WIDTH_STEP ? next : was));
      }, STAND_IN_SETTLE_MS);
    };
    settle(Math.round(element.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return () => timer && clearTimeout(timer);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) settle(Math.round(entry.contentRect.width));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Ready while the control's CURRENT frame has loaded. A frame the control swaps in (a redraw at
  // a new width) is not loaded yet, so the skeleton comes back until it is — the face must never
  // promise a press the frame underneath cannot take.
  useEffect(() => {
    if (!wait || !control.current) return undefined;
    const root = control.current;
    const loaded = new WeakSet<HTMLIFrameElement>();
    const watched = new WeakSet<HTMLIFrameElement>();
    const check = () => {
      const frames = Array.from(root.querySelectorAll('iframe'));
      for (const frame of frames) {
        if (watched.has(frame)) continue;
        watched.add(frame);
        frame.addEventListener(
          'load',
          () => {
            loaded.add(frame);
            check();
          },
          { once: true },
        );
      }
      setReady(frames.length > 0 && frames.every((frame) => loaded.has(frame)));
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [wait]);

  const content = typeof children === 'function' ? (width ? children(width) : null) : children;
  return (
    <div ref={box} className={cx('a-stand-in', !ready && 'a-stand-in--waiting')}>
      <div className="a-stand-in__face" aria-hidden="true">
        {ready ? face : <Skeleton shape="button" />}
      </div>
      <div ref={control} className="a-stand-in__control" inert={!ready}>
        {content}
      </div>
    </div>
  );
}
